import React, { useEffect, useState } from "react"

import { Box, HStack, Icon, Spinner, Text } from "wealthmode-frontend/gluestack/ui"
import { DatabaseIcon, type LucideIcon } from "wealthmode-frontend/icons"

import type {
  DataTableFilters,
  DataTableHeader,
  RawDataTablePagination,
  RawDataTableSearch,
} from "@/components/DataTable"
import { TableCardPagination } from "@/components/TableCardPagination"
import { TableFilters } from "@/components/TableFilters"
import { TableScrollLayout } from "@/components/TableScrollLayout"
import { TableSearchInput } from "@/components/TableSearchInput"

export type {
  DataTableFilters,
  DataTableHeader,
  RawDataTablePagination,
  RawDataTableSearch,
} from "@/components/DataTable"

function columnClass(hdr: DataTableHeader, cell: "th" | "td") {
  const classes = ["px-4", "align-middle", hdr.center ? "text-center" : "text-left"]
  const fitContent = hdr.shrink || hdr.noTruncate

  if (!fitContent) {
    classes.push("min-w-0")
  }

  if (cell === "th") {
    classes.push(
      "sticky",
      "top-0",
      "z-10",
      "h-11",
      "text-sm",
      "font-medium",
      "leading-none",
      "text-foreground-tertiary",
      "border-b border-border-medium",
      "bg-background-tertiary",
    )
  } else {
    classes.push("py-3", "text-sm", "text-foreground-secondary")
  }

  if (fitContent) {
    classes.push("w-0", "whitespace-nowrap")
  } else if (hdr.wrap) {
    classes.push("whitespace-normal", "break-words")
  } else {
    classes.push("whitespace-nowrap", "overflow-hidden", "text-ellipsis")
  }

  return classes.join(" ")
}

export default function RawDataTable<Row>({
  loading,
  rows,
  title,
  titleIcon = DatabaseIcon,
  header,
  render,
  pagination,
  search,
  filters,
}: {
  loading: boolean
  rows: Row[]
  title: string
  titleIcon?: LucideIcon
  header: Array<DataTableHeader>
  render: (row: Row, field: string) => any
  pagination?: RawDataTablePagination
  search?: RawDataTableSearch
  filters?: DataTableFilters
}) {
  const [pending, setPending] = useState<boolean>(loading)
  const [list, setList] = useState<Row[]>(rows)

  useEffect(() => {
    setPending(loading)
  }, [loading])

  useEffect(() => {
    setList(rows)
  }, [rows])

  function renderCell(row: Row, hdr: DataTableHeader) {
    const content = render(row, hdr.id)

    if (content == null || content === "") {
      return content
    }

    const inner = React.isValidElement(content) ? (
      content
    ) : (
      <Text className="text-sm text-foreground-secondary text-left">{String(content)}</Text>
    )

    const alignClass = hdr.center ? "text-center mx-auto" : "text-left"
    const wrapClass = hdr.wrap
      ? `block w-full whitespace-normal break-words ${alignClass}`
      : hdr.noTruncate || hdr.shrink
        ? `block w-max max-w-full whitespace-nowrap ${alignClass}`
        : `block w-full min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap ${alignClass}`

    return <Box className={wrapClass}>{inner}</Box>
  }

  return (
    <Box className="bg-background-secondary rounded-lg border border-border-medium flex flex-1 min-h-0 flex-col overflow-hidden w-full min-w-0">
      <Box className="px-6 py-4 border-b border-border-medium shrink-0">
        {search ? (
          <HStack className="w-full min-w-0 items-center">
            <HStack className="min-w-24 flex-1 items-center justify-start gap-2 overflow-hidden">
              <Icon
                as={titleIcon}
                className="text-foreground-tertiary shrink-0 h-10 w-10"
              />
              <Text className="text-lg font-semibold text-foreground-primary truncate">{title}</Text>
            </HStack>
            <Box className="flex min-w-0 flex-1 flex-row items-center justify-center px-2">
              <Box className="w-full min-w-50 max-w-200">
                <TableSearchInput
                  mode={search.mode}
                  value={search.value}
                  onChangeText={search.onChangeText}
                />
              </Box>
            </Box>
            <Box className="flex min-w-0 flex-1 flex-row items-center justify-end gap-2">
              {filters ? (
                <TableFilters
                  title={title}
                  filters={filters}
                />
              ) : null}
              {pagination ? (
                <TableCardPagination
                  showPrev={pagination.showPrev}
                  showNext={pagination.showNext}
                  onPrev={pagination.onPrev}
                  onNext={pagination.onNext}
                  onRefresh={pagination.onRefresh}
                  refreshDisabled={pagination.refreshDisabled}
                />
              ) : null}
            </Box>
          </HStack>
        ) : (
          <HStack className="justify-between items-center gap-4">
            <HStack className="items-center gap-2 min-w-0">
              <Icon
                as={titleIcon}
                className="text-foreground-tertiary shrink-0 h-10 w-10"
              />
              <Text className="text-lg font-semibold text-foreground-primary">{title}</Text>
            </HStack>
            <HStack className="items-center gap-2">
              {filters ? (
                <TableFilters
                  title={title}
                  filters={filters}
                />
              ) : null}
              {pagination ? (
                <TableCardPagination
                  showPrev={pagination.showPrev}
                  showNext={pagination.showNext}
                  onPrev={pagination.onPrev}
                  onNext={pagination.onNext}
                  onRefresh={pagination.onRefresh}
                  refreshDisabled={pagination.refreshDisabled}
                />
              ) : null}
            </HStack>
          </HStack>
        )}
      </Box>

      <TableScrollLayout
        header={header}
        headerRow={header.map((hdr: DataTableHeader) => (
          <th
            key={hdr.id}
            className={columnClass(hdr, "th")}
          >
            {hdr.title}
          </th>
        ))}
      >
        {pending ? (
          <tr>
            <td
              colSpan={header.length}
              className="px-4 py-8 text-center"
            >
              <Box className="flex justify-center">
                <Spinner />
              </Box>
            </td>
          </tr>
        ) : list.length == 0 ? (
          <tr>
            <td
              colSpan={header.length}
              className="px-4 py-8 text-center text-foreground-tertiary"
            >
              No Data
            </td>
          </tr>
        ) : (
          list.map((row: Row, index: number) => (
            <tr
              key={index}
              className="hover:bg-background-tertiary/30 transition-colors"
            >
              {header.map((hdr: DataTableHeader) => (
                <td
                  key={hdr.id}
                  className={columnClass(hdr, "td")}
                >
                  {renderCell(row, hdr)}
                </td>
              ))}
            </tr>
          ))
        )}
      </TableScrollLayout>
    </Box>
  )
}
